import React, { useState } from 'react';
import { Button } from '@carbon/react';
import { TableActions } from 'components/table/table-actions';
import styles from './tanstack-table-styles.module.scss';
import tableActionStyles from 'components/table/table-actions.module.scss';
import { ThresholdFilterMenu } from 'components/allocation-manager/ownership-tables';
import { Data_1 } from '@carbon/react/icons'; // eslint-disable-line camelcase
import { ParticipationFactorMenu } from 'components/allocation-manager/ownership-tables/participation-factor-menu';
import { DebouncedInput } from 'components/debounced-input';
import classNames from 'classnames';

interface TableControlsProps {
  table: any;
  globalFilter: string;
  setGlobalFilter: (value: string) => void;
  isColumnFilterActive: boolean;
  setIsColumnFilterActive: (value: boolean) => void;
  // Optional export props
  exportData?: any[];
  headers?: any[];
  filename?: string;
  ExportButton?: React.ComponentType<any>;
  currentThreshold: number;
  onThresholdChange: (threshold: number) => void;
  onThresholdReset: () => void;
  participationFactorThreshold: number | null;
  onParticipationFactorThresholdChange: (threshold: number | null) => void;
}

export function TableControls({
  table,
  globalFilter,
  setGlobalFilter,
  isColumnFilterActive,
  setIsColumnFilterActive,
  exportData,
  headers,
  filename,
  ExportButton,
  currentThreshold = 3,
  onThresholdChange,
  onThresholdReset,
  participationFactorThreshold,
  onParticipationFactorThresholdChange,
}: TableControlsProps) {
  const [participationFactorMenuOpen, setParticipationFactorMenuOpen] =
    useState(false);

  return (
    <div className={styles.tableContext}>
      <div className={styles.searchContainer}>
        <DebouncedInput
          value={globalFilter ?? ''}
          onChange={(value) => setGlobalFilter(String(value).trim())}
          placeholder="Search columns..."
          className={styles.allocationManagerSearch}
          data-testid="global-search-input"
          debounce={300}
        />
      </div>
      <div className={styles.actionButtons}>
        <div>
          <Button
            hasIconOnly={!participationFactorThreshold}
            onClick={() =>
              setParticipationFactorMenuOpen(!participationFactorMenuOpen)
            }
            kind="ghost"
            iconDescription="PTF Adjustment"
            renderIcon={Data_1} // eslint-disable-line camelcase
            className={classNames(
              tableActionStyles.actionButton,
              tableActionStyles.ptfAdjustmentButton,
              { [styles.activeButton]: participationFactorThreshold },
              { 'overlap-button': participationFactorThreshold }
            )}
            data-testid="table-ptf-button"
          >
            {participationFactorThreshold &&
              `${participationFactorThreshold * 100}% Adjustment`}
          </Button>
          {participationFactorMenuOpen && (
            <ParticipationFactorMenu
              participationFactorThreshold={participationFactorThreshold}
              onClose={() => setParticipationFactorMenuOpen(false)}
              onParticipationFactorThresholdChange={
                onParticipationFactorThresholdChange
              }
            />
          )}
        </div>
        <ThresholdFilterMenu
          currentThreshold={currentThreshold}
          onApply={onThresholdChange}
          onReset={onThresholdReset}
        />
        <TableActions
          table={table}
          isColumnFilterActive={isColumnFilterActive}
          setIsColumnFilterActive={setIsColumnFilterActive}
        />
        {ExportButton && exportData && headers && filename && (
          <ExportButton
            headers={headers}
            rows={exportData}
            filename={filename}
            className={styles.exportButton}
          />
        )}
      </div>
    </div>
  );
}
